import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateRaceInput, Race, RaceStatus, UpdateRaceInput } from './hooks';

const RACE_TYPES = [
    'Triathlon Sprint',
    'Triathlon Olympic',
    'Ironman 70.3',
    'Ironman',
    'Marathon',
    'Half Marathon',
    '10k Run',
    'Gran Fondo',
    'Ciclosportiva',
    'Altro',
];

const CUSTOM_TYPE = '__custom__';

interface Props {
    isOpen: boolean;
    initialValues?: Race;
    onClose: () => void;
    onSubmit: (data: CreateRaceInput | UpdateRaceInput) => Promise<void>;
}

const empty = {
    raceType: '',
    date: '',
    location: '',
    notes: '',
    status: 'planned' as RaceStatus,
    finishTime: '',
    position: '',
    achievement: '',
};

const RaceModal: React.FC<Props> = ({ isOpen, initialValues, onClose, onSubmit }) => {
    const isEditing = Boolean(initialValues);
    const { t } = useTranslation();

    const STATUS_OPTIONS: { value: RaceStatus; label: string }[] = [
        { value: 'planned', label: t('races.statusPlanned') },
        { value: 'registered', label: t('races.statusRegistered') },
        { value: 'confirmed', label: t('races.statusConfirmed') },
        { value: 'completed', label: t('races.statusCompleted') },
        { value: 'cancelled', label: t('races.statusCancelled') },
    ];

    const [form, setForm] = useState(empty);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Race type: dropdown selection + free text when "custom" is chosen
    const [typeSelection, setTypeSelection] = useState('');
    const [customType, setCustomType] = useState('');
    const isCustomType = typeSelection === CUSTOM_TYPE;

    useEffect(() => {
        if (initialValues) {
            setForm({
                raceType: initialValues.raceType,
                date: initialValues.date,
                location: initialValues.location,
                notes: initialValues.notes ?? '',
                status: initialValues.status,
                finishTime: initialValues.finishTime ?? '',
                position: initialValues.position ?? '',
                achievement: initialValues.achievement ?? '',
            });
            // A saved type that isn't in the list was entered as a custom one
            const known = RACE_TYPES.includes(initialValues.raceType);
            setTypeSelection(known ? initialValues.raceType : CUSTOM_TYPE);
            setCustomType(known ? '' : initialValues.raceType);
        } else {
            setForm(empty);
            setTypeSelection('');
            setCustomType('');
        }
        setError('');
    }, [initialValues, isOpen]);

    if (!isOpen) return null;

    const set = (field: keyof typeof form, value: string) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const handleTypeChange = (value: string) => {
        setTypeSelection(value);
        set('raceType', value === CUSTOM_TYPE ? customType : value);
    };

    const handleCustomTypeChange = (value: string) => {
        setCustomType(value);
        set('raceType', value);
    };

    const isCompleted = form.status === 'completed';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.raceType.trim() || !form.date || !form.location.trim()) {
            setError(t('races.validationRequired'));
            return;
        }
        setLoading(true);
        setError('');
        try {
            const payload: CreateRaceInput = {
                raceType: form.raceType.trim(),
                date: form.date,
                location: form.location.trim(),
                status: form.status,
                notes: form.notes.trim() || undefined,
                finishTime: isCompleted && form.finishTime.trim() ? form.finishTime.trim() : undefined,
                position: isCompleted && form.position.trim() ? form.position.trim() : undefined,
                achievement: isCompleted && form.achievement.trim() ? form.achievement.trim() : undefined,
            };
            await onSubmit(payload);
            onClose();
        } catch {
            setError(t('races.validationError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90dvh]">

                {/* Header */}
                <div className="px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white">
                            <i className={`fa-solid ${isEditing ? 'fa-pen-to-square' : 'fa-flag-checkered'}`}></i>
                        </div>
                        <div>
                            <h2 className="text-white font-bold text-base">
                                {isEditing ? t('races.modalEditTitle') : t('races.modalAddTitle')}
                            </h2>
                            <p className="text-cyan-100 text-xs">
                                {isEditing ? t('races.modalEditSubtitle') : t('races.modalAddSubtitle')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full text-white/80 hover:text-white hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <i className="fa-solid fa-xmark text-sm"></i>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 md:p-6 flex flex-col gap-3 md:gap-4 overflow-y-auto">

                    {/* Race Type + Status row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {t('races.fieldType')} <span className="text-red-400">*</span>
                            </label>
                            <select
                                value={typeSelection}
                                onChange={(e) => handleTypeChange(e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 bg-white"
                            >
                                <option value="" disabled>{t('races.selectType')}</option>
                                {RACE_TYPES.map((rt) => (
                                    <option key={rt} value={rt}>{rt}</option>
                                ))}
                                <option value={CUSTOM_TYPE}>{t('races.customType')}</option>
                            </select>

                            {isCustomType && (
                                <input
                                    type="text"
                                    autoFocus
                                    value={customType}
                                    onChange={(e) => handleCustomTypeChange(e.target.value)}
                                    placeholder={t('races.customTypePlaceholder')}
                                    className="mt-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 bg-white"
                                />
                            )}
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('races.fieldStatus')}</label>
                            <select
                                value={form.status}
                                onChange={(e) => set('status', e.target.value as RaceStatus)}
                                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 bg-white"
                            >
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Date + Location row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {t('races.fieldDate')} <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="date"
                                value={form.date}
                                onChange={(e) => set('date', e.target.value)}
                                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                            />
                        </div>

                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                                {t('races.fieldLocation')} <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={form.location}
                                onChange={(e) => set('location', e.target.value)}
                                placeholder={t('races.fieldLocationPlaceholder')}
                                className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('races.fieldNotes')}</label>
                        <input
                            type="text"
                            value={form.notes}
                            onChange={(e) => set('notes', e.target.value)}
                            placeholder={t('races.fieldNotesPlaceholder')}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400"
                        />
                    </div>

                    {/* Completed fields */}
                    {isCompleted && (
                        <div className="border border-green-200 rounded-xl p-4 bg-green-50 flex flex-col gap-3">
                            <p className="text-xs font-bold text-green-700 uppercase tracking-wide flex items-center gap-1">
                                <i className="fa-solid fa-flag-checkered"></i> {t('races.resultSection')}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wide">{t('races.fieldTime')}</label>
                                    <input
                                        type="text"
                                        value={form.finishTime}
                                        onChange={(e) => set('finishTime', e.target.value)}
                                        placeholder="HH:MM:SS"
                                        inputMode="numeric"
                                        maxLength={8}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-400 font-mono"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wide">{t('races.fieldPosition')}</label>
                                    <input
                                        type="text"
                                        value={form.position}
                                        onChange={(e) => set('position', e.target.value)}
                                        placeholder={t('races.fieldPositionPlaceholder')}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-slate-500 font-bold uppercase tracking-wide">{t('races.fieldAchievement')}</label>
                                    <input
                                        type="text"
                                        value={form.achievement}
                                        onChange={(e) => set('achievement', e.target.value)}
                                        placeholder={t('races.fieldAchievementPlaceholder')}
                                        className="px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                            {error}
                        </p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-3 pt-1">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors font-medium"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-5 py-2 bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white rounded-lg font-bold text-sm transition-colors shadow-sm"
                        >
                            <i className={`fa-solid ${isEditing ? 'fa-pen-to-square' : 'fa-floppy-disk'}`}></i>
                            {loading ? t('common.saving') : isEditing ? t('common.update') : t('common.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RaceModal;
